import { createTheme } from "@mui/material/styles";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#2563eb",
      dark: "#1d4ed8",
      light: "#3b82f6",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#7c3aed",
      dark: "#6d28d9",
      light: "#8b5cf6",
    },
    background: {
      default: "#F3F4F6",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#111827",
      secondary: "#6B7280",
    },
    divider: "rgba(0, 0, 0, 0.08)",
    grey: {
      50: "#F9FAFB",
      100: "#F3F4F6",
      200: "#E5E7EB",
      300: "#D1D5DB",
      400: "#9CA3AF",
      500: "#6B7280",
      600: "#4B5563",
      700: "#374151",
      800: "#1F2937",
      900: "#111827",
    },
    action: {
      hover: "rgba(37, 99, 235, 0.04)",
      selected: "rgba(37, 99, 235, 0.08)",
    },
  },
  typography: {
    fontFamily:
      "'Inter', 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    // Explicit, documented type scale = single source of truth.
    // Prefer these variants over hardcoding fontSize in component `sx`.
    h1: {
      fontSize: "2.5rem",
      fontWeight: 700,
      letterSpacing: "-0.025em",
      lineHeight: 1.2,
    },
    h2: {
      fontSize: "2rem",
      fontWeight: 700,
      letterSpacing: "-0.025em",
      lineHeight: 1.25,
    },
    h3: {
      fontSize: "1.5rem",
      fontWeight: 700,
      letterSpacing: "-0.02em",
      lineHeight: 1.3,
    },
    h4: {
      fontSize: "1.25rem",
      fontWeight: 600,
      letterSpacing: "-0.015em",
      lineHeight: 1.35,
    },
    h5: {
      fontSize: "1.125rem",
      fontWeight: 600,
      letterSpacing: "-0.01em",
      lineHeight: 1.4,
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 600,
      letterSpacing: "-0.005em",
      lineHeight: 1.4,
    },
    subtitle1: { fontSize: "0.9375rem", fontWeight: 500, lineHeight: 1.5 },
    subtitle2: { fontSize: "0.8125rem", fontWeight: 500, lineHeight: 1.5 },
    body1: { fontSize: "1rem", lineHeight: 1.6 },
    body2: { fontSize: "0.875rem", lineHeight: 1.5 },
    caption: { fontSize: "0.75rem", lineHeight: 1.4 },
    overline: {
      fontSize: "0.6875rem",
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      lineHeight: 1.4,
    },
    button: {
      textTransform: "none",
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    "none",
    "0 1px 2px rgba(0, 0, 0, 0.05)",
    "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    "0 6px 8px -2px rgba(0, 0, 0, 0.1), 0 3px 5px -2px rgba(0, 0, 0, 0.06)",
    "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    "0 12px 18px -4px rgba(0, 0, 0, 0.1), 0 5px 8px -3px rgba(0, 0, 0, 0.05)",
    "0 14px 20px -5px rgba(0, 0, 0, 0.11), 0 6px 9px -4px rgba(0, 0, 0, 0.05)",
    "0 16px 24px -6px rgba(0, 0, 0, 0.12), 0 7px 10px -4px rgba(0, 0, 0, 0.05)",
    "0 18px 26px -6px rgba(0, 0, 0, 0.12), 0 8px 11px -5px rgba(0, 0, 0, 0.05)",
    "0 20px 25px -5px rgba(0, 0, 0, 0.12), 0 10px 10px -5px rgba(0, 0, 0, 0.05)",
    "0 21px 28px -6px rgba(0, 0, 0, 0.13), 0 11px 12px -5px rgba(0, 0, 0, 0.05)",
    "0 22px 30px -7px rgba(0, 0, 0, 0.13), 0 12px 14px -6px rgba(0, 0, 0, 0.05)",
    "0 23px 32px -7px rgba(0, 0, 0, 0.14), 0 13px 16px -6px rgba(0, 0, 0, 0.05)",
    "0 24px 34px -8px rgba(0, 0, 0, 0.14), 0 14px 18px -7px rgba(0, 0, 0, 0.05)",
    "0 25px 36px -8px rgba(0, 0, 0, 0.15), 0 15px 20px -7px rgba(0, 0, 0, 0.05)",
    "0 26px 38px -9px rgba(0, 0, 0, 0.15), 0 16px 22px -8px rgba(0, 0, 0, 0.05)",
    "0 27px 40px -9px rgba(0, 0, 0, 0.16), 0 17px 24px -8px rgba(0, 0, 0, 0.05)",
    "0 28px 42px -10px rgba(0, 0, 0, 0.16), 0 18px 26px -9px rgba(0, 0, 0, 0.05)",
    "0 29px 44px -10px rgba(0, 0, 0, 0.17), 0 19px 28px -9px rgba(0, 0, 0, 0.05)",
    "0 30px 46px -11px rgba(0, 0, 0, 0.18), 0 20px 30px -10px rgba(0, 0, 0, 0.05)",
    "0 31px 48px -11px rgba(0, 0, 0, 0.19), 0 21px 32px -10px rgba(0, 0, 0, 0.05)",
    "0 32px 50px -12px rgba(0, 0, 0, 0.2), 0 22px 34px -11px rgba(0, 0, 0, 0.05)",
    "0 33px 52px -12px rgba(0, 0, 0, 0.22), 0 23px 36px -11px rgba(0, 0, 0, 0.05)",
    "0 34px 54px -12px rgba(0, 0, 0, 0.24), 0 24px 38px -12px rgba(0, 0, 0, 0.05)",
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          borderRadius: 8,
          lineHeight: 1.5,
        },
        contained: {
          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
          "&:hover": {
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow:
            "0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)",
          border: "1px solid rgba(0, 0, 0, 0.06)",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
        rounded: {
          borderRadius: 12,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 6,
          fontSize: "12px",
          fontWeight: 500,
          backgroundColor: "#1F2937",
          padding: "6px 10px",
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 700,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          border: "none",
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          fontSize: "14px",
          minHeight: 44,
          color: "#6B7280",
          "&.Mui-selected": {
            color: "#111827",
            fontWeight: 600,
          },
        },
      },
    },
  },
});

export default theme;
